/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import React from 'react';
import Popover from '@mui/material/Popover';
import useMediaQuery from '@mui/material/useMediaQuery';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

import styles from './MessageTooltipDisplayer.module.scss';
import { RenderedHTML } from './RenderedHtml/RenderedHTML';

export type TooltipType = {
  element?: HTMLElement;
  text: string;
  header?: string;
};

interface MessageTooltipDisplayerProps {
  compact: boolean;
  onClose: () => void;
  tooltip?: TooltipType;
  pageId?: string;
  documentId?: string;
  documentVersionId?: string;
  documentSlug?: string;
}

export function MessageTooltipDisplayer({
  compact,
  onClose,
  tooltip,
  documentId,
  documentVersionId,
  documentSlug,
  pageId,
}: MessageTooltipDisplayerProps) {
  const isSmallScreen = useMediaQuery('(max-width: 1000px)');

  if (!tooltip) return null;
  const content = tooltip && (
    <div className={styles.tooltipInner}>
      <IconButton onClick={onClose} className={styles.closeButton}>
        <CloseIcon />
      </IconButton>
      <div className={styles.tooltipHeader}>{tooltip.header}</div>
      <RenderedHTML
        content={tooltip.text}
        onClick={onClose}
        pageId={pageId}
        documentVersionId={documentVersionId}
        documentId={documentId}
        documentSlug={documentSlug}
      />
    </div>
  );
  if (isSmallScreen) {
    return (
      <Popover
        open={!!tooltip}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        onClose={onClose}
      >
        <div className={styles.tooltipMobile} onClick={onClose}>
          {content}
        </div>
      </Popover>
    );
  } else if (compact) {
    /* Note we can't do:  anchorEl={tooltip.element}    because we get:
     * The `anchorEl` prop provided to the component is invalid.
     * Because the img that we display gets replaced.  It's the same bug we have where the img
     * icon flickers, which I haven't resolved.
     */
    return (
      <Popover
        open={!!tooltip}
        onClose={onClose}
        anchorEl={tooltip.element}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
      >
        <div className={styles.tooltipCompact} onClick={onClose}>
          {content}
        </div>
      </Popover>
    );
  } else {
    return <div className={styles.tooltipDesktop}>{content}</div>;
  }
}
